import { useEffect, useState, useMemo, useRef } from 'react';
import {
  useReactTable,
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
} from '@tanstack/react-table';
import { is, List, Record, RecordOf } from 'immutable';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';

// ASSETS
import TaxesIcon from 'assets/TaxesIcon';

// CONSTANTS
import { USER_ROLES } from 'constants/common';

// HOOKS
import { useAppDispatch } from 'hooks/store';

// SELECTORS
import { selectTaxes, selectTaxesErrors, selectFleetSettings } from 'services/FleetSettings/selectors';
import { selectUser } from 'services/Authentication/selectors';

// THUNKS
import { getFleetSettings, getTaxes, updateSettings } from 'services/FleetSettings/thunks';

// TYPES
import { AuthUser, ChargeCalculator, FleetSettings, Tax } from 'services/resources';

// COMPONENTS
import RadioButtons from 'components/RadioButtons';
import TaxButton from './components/TaxButton';

// STYLES
import { Section, SectionHeader, SectionContent, SectionHeaderTitle } from 'styles/cards';
import colors from 'styles/colors';
import { SortableHeader, SortingIcon, StyledActionButtonsContainer, StyledSortableIcon } from 'components/SectionTable';
import { ChargeCatalogueTableContainer } from './styles';

type TaxItemType = { id: number; renderedValue: { translationId: string }; value: boolean };

const taxesFactory = Record<TaxItemType>({
  id: 0,
  renderedValue: { translationId: '' },
  value: false,
});

const APPLY_TAX_ITEMS = List([
  taxesFactory({
    id: 0,
    renderedValue: { translationId: 'views.FleetSettings.Taxes.apply' },
    value: true,
  }),
  taxesFactory({
    id: 1,
    renderedValue: { translationId: 'views.FleetSettings.Taxes.dontApply' },
    value: false,
  }),
]);

export const ApplyTaxesRadioButtons = ({ handleClick }: { handleClick: (item: RecordOf<TaxItemType>) => void }) => {
  const { applyTaxes, chargeCalculator } = useSelector(selectFleetSettings());

  if (typeof chargeCalculator !== 'undefined' && chargeCalculator !== ChargeCalculator.TRANSACTION) {
    return null;
  }

  return (
    <RadioButtons
      data-testid="applyTaxes"
      items={APPLY_TAX_ITEMS}
      selectedItemId={applyTaxes ? 0 : 1}
      onSelect={handleClick}
      title="views.FleetSettings.Taxes.applyTaxes"
    />
  );
};

const TaxesTable = () => {
  const dispatch = useAppDispatch();
  const taxes: List<Tax> = useSelector(selectTaxes());
  const settings: FleetSettings = useSelector(selectFleetSettings());
  const currentUser: AuthUser = useSelector(selectUser());
  const errors: any = useSelector(selectTaxesErrors());

  // Local state for tax edits and edit mode.
  const [localTaxes, setLocalTaxes] = useState(taxes);
  const [sorting, setSorting] = useState([]);

  const columnHelper = createColumnHelper<Tax>();

  // Mimic componentDidMount: load fleet settings and taxes.
  useEffect(() => {
    dispatch(getFleetSettings());
    dispatch(getTaxes());
  }, [dispatch]);

  // Update localTaxes when taxes change.
  useEffect(() => {
    if (!is(taxes, localTaxes)) {
      setLocalTaxes(taxes);
    }
  }, [localTaxes, taxes]);

  // Convert Immutable list to plain JS array for TanStack Table.
  const tableData = useMemo(() => localTaxes.toJS(), [localTaxes]);

  // Define columns for tax number, description, code, and rate.
  const columns = useMemo(
    () => [
      columnHelper.accessor('taxNumber', {
        header: () => <FormattedMessage id="views.FleetSettings.Taxes.number" />,
        cell: (info) => {
          return info.getValue();
        },
      }),
      columnHelper.accessor('description', {
        header: () => <FormattedMessage id="views.FleetSettings.Taxes.description" />,
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor('code', {
        header: () => <FormattedMessage id="views.FleetSettings.Taxes.code" />,
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor('rate', {
        header: () => <FormattedMessage id="views.FleetSettings.Taxes.rate" />,
        cell: (info) => `${Number(info.getValue())}%`,
      }),
      columnHelper.display({
        id: 'actions',
        header: () => null,
        enableResizing: false,
        size: 48,
        cell: ({ row }) => {
          const tax = row.original;

          return tax ? <StyledActionButtonsContainer>{<TaxButton tax={tax} />}</StyledActionButtonsContainer> : null;
        },
      }),
    ],
    [columnHelper]
  );

  const columnVisibility = {
    actions: currentUser.profile.role in [USER_ROLES.admin, USER_ROLES.user],
  };

  // Initialize the table instance using TanStack React Table.
  const taxTable = useReactTable({
    data: tableData,
    columns,
    state: {
      columnVisibility,
      sorting,
    },
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    getCoreRowModel: getCoreRowModel(),
  });

  const taxTableContainerRef = useRef(null);

  const handleApplyTaxesClicked = (item) => {
    if (item.get('value') !== settings.applyTaxes) {
      dispatch(
        updateSettings(settings.id, {
          applyTaxes: Boolean(item.get('value')),
        })
      );
    }
  };

  if (errors.count() > 0) {
    return <>{errors.get('detail')}</>;
  }

  const showSortingIcons = (canSort, isSorted) => {
    if (canSort && !isSorted) {
      return <StyledSortableIcon size={16} color={colors.lightGrayIcon} />;
    }
    if (isSorted) {
      return (
        <SortingIcon
          size={14}
          color={colors.fleetDarkGray}
          direction={
            {
              asc: SortingIcon.Direction.Up,
              desc: SortingIcon.Direction.Down,
            }[isSorted]
          }
        />
      );
    }
    return null;
  };

  return (
    <Section>
      <SectionHeader>
        <SectionHeaderTitle>
          <TaxesIcon color={colors.fleetBlue} />
          <FormattedMessage id="views.FleetSettings.Taxes.title" />
        </SectionHeaderTitle>
      </SectionHeader>
      <SectionContent>
        <ApplyTaxesRadioButtons handleClick={handleApplyTaxesClicked} />
        <ChargeCatalogueTableContainer ref={taxTableContainerRef}>
          <table>
            <thead>
              {taxTable.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    const isSorted = header.column.getIsSorted();
                    const canSort = header.column.getCanSort();
                    return (
                      <SortableHeader
                        key={header.id}
                        $sortable={canSort}
                        style={{
                          width: header.getSize() === Number.MAX_SAFE_INTEGER ? undefined : header.getSize(),
                        }}
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        {header.isPlaceholder || header.id === 'actions' ? null : (
                          <div>
                            {flexRender(header.column.columnDef.header, header.getContext())}
                            {showSortingIcons(canSort, isSorted)}
                          </div>
                        )}
                      </SortableHeader>
                    );
                  })}
                </tr>
              ))}
            </thead>
            <tbody>
              {taxTable.getRowModel().rows.map((row) => (
                <tr key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </ChargeCatalogueTableContainer>
      </SectionContent>
    </Section>
  );
};

export default TaxesTable;
